import { useEffect, useState } from "react";
import { api } from "@/lib/apiClient";
import { useWorkspace } from "@/context/WorkspaceContext";
import { PageHeader, StatCard, EmptyState } from "@/components/PageBits";
import { Button } from "@/components/ui/button";
import { Clock, Mic, Video, Activity } from "lucide-react";
import {
  AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid,
} from "recharts";

export default function Usage() {
  const { projectId, currentProject } = useWorkspace();
  const [summary, setSummary] = useState(null);
  const [series, setSeries] = useState([]);
  const [events, setEvents] = useState({ items: [], page: 1, total_pages: 1, total: 0 });
  const [page, setPage] = useState(1);

  useEffect(() => {
    if (!projectId) return;
    api.get(`/projects/${projectId}/usage`).then((r) => { setSummary(r.data.summary); setSeries(r.data.series); }).catch(() => {});
  }, [projectId]);
  useEffect(() => {
    if (!projectId) return;
    api.get(`/projects/${projectId}/usage/events?page=${page}&page_size=15`).then((r) => setEvents(r.data)).catch(() => {});
  }, [projectId, page]);

  const chartData = series.map((s, i) => ({ t: i + 1, minutes: s.minutes, audio: s.audio, video: s.video }));

  if (!projectId) return <EmptyState title="No project selected" description="Select a project first." />;

  return (
    <div data-testid="usage-page">
      <PageHeader title="Usage" description={`Metered from the append-only ledger for ${currentProject?.name || "your project"}.`} />

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <StatCard testId="usage-participant-minutes" label="Participant min" value={summary?.participant_minutes ?? 0} icon={Clock} />
        <StatCard testId="usage-audio-minutes" label="Audio min" value={summary?.audio_minutes ?? 0} icon={Mic} />
        <StatCard testId="usage-video-minutes" label="Video min" value={summary?.video_minutes ?? 0} icon={Video} />
        <StatCard testId="usage-joins" label="Total joins" value={summary?.total_joins ?? 0} icon={Activity} />
      </div>

      <div className="mt-8 rounded-md border border-zinc-800 bg-zinc-900/30 p-5">
        <h2 className="mb-4 text-sm font-medium text-zinc-200">Minutes over time</h2>
        {chartData.length === 0 ? (
          <div className="py-12 text-center text-sm text-zinc-500">No metered minutes yet. Add participants to a room — the ledger records a minute per active participant.</div>
        ) : (
          <ResponsiveContainer width="100%" height={240}>
            <AreaChart data={chartData}>
              <defs>
                <linearGradient id="g1" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#10B981" stopOpacity={0.5} />
                  <stop offset="100%" stopColor="#10B981" stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#27272A" vertical={false} />
              <XAxis dataKey="t" stroke="#52525B" fontSize={11} tickLine={false} axisLine={false} />
              <YAxis stroke="#52525B" fontSize={11} tickLine={false} axisLine={false} allowDecimals={false} />
              <Tooltip contentStyle={{ background: "#09090B", border: "1px solid #27272A", borderRadius: 8, fontSize: 12 }} labelStyle={{ color: "#A1A1AA" }} />
              <Area type="monotone" dataKey="minutes" stroke="#10B981" strokeWidth={2} fill="url(#g1)" />
            </AreaChart>
          </ResponsiveContainer>
        )}
      </div>

      <div className="mt-8 rounded-md border border-zinc-800 bg-zinc-900/30">
        <div className="flex items-center justify-between border-b border-zinc-800 px-5 py-3">
          <h2 className="text-sm font-medium text-zinc-200">Ledger events</h2>
          <span className="font-mono text-xs text-zinc-500">{events.total} events · append-only</span>
        </div>
        {events.items.length === 0 ? (
          <div className="px-5 py-10 text-center text-sm text-zinc-500">No events yet.</div>
        ) : (
          <table className="w-full text-sm" data-testid="ledger-table">
            <thead className="text-left text-xs uppercase tracking-wider text-zinc-500">
              <tr><th className="px-5 py-2 font-medium">Type</th><th className="px-5 py-2 font-medium">Room</th><th className="px-5 py-2 font-medium">Participant</th><th className="px-5 py-2 font-medium">Source</th><th className="px-5 py-2 font-medium">Time</th></tr>
            </thead>
            <tbody className="divide-y divide-zinc-800 font-mono text-xs">
              {events.items.map((e, i) => (
                <tr key={i} data-testid={`ledger-event-${i}`}>
                  <td className="px-5 py-2 text-zinc-200">{e.type}</td>
                  <td className="px-5 py-2 text-zinc-400">{e.room_name || "—"}</td>
                  <td className="px-5 py-2 text-zinc-400">{e.participant_id || "—"}</td>
                  <td className="px-5 py-2"><span className="rounded border border-zinc-800 px-1.5 py-0.5 text-[10px] text-zinc-500">{e.source}</span></td>
                  <td className="px-5 py-2 text-zinc-500">{(e.created_at || "").slice(11, 19)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
        {events.total_pages > 1 && (
          <div className="flex items-center justify-between border-t border-zinc-800 px-5 py-3">
            <Button data-testid="ledger-prev" variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage((p) => p - 1)} className="border-zinc-800 bg-zinc-950">Prev</Button>
            <span className="text-xs text-zinc-500">Page {events.page} / {events.total_pages}</span>
            <Button data-testid="ledger-next" variant="outline" size="sm" disabled={page >= events.total_pages} onClick={() => setPage((p) => p + 1)} className="border-zinc-800 bg-zinc-950">Next</Button>
          </div>
        )}
      </div>
    </div>
  );
}
